import { format } from "date-fns";

import {
  type EventFieldSchema,
  parseFieldSchema,
} from "@/components/event-records/event-record-schema";
import { formatFieldValue } from "@/lib/event-records/format-field-value";

interface EventRecordDocumentProps {
  payload: Record<string, unknown>;
}

/**
 * Renders an incident record for reading before signing (AC11).
 *
 * Uses the same field grid and value formatter as the record's own detail page,
 * so the document a staff member signs looks like the document a manager reads —
 * which matters when the signature is permanent and says "I have read and
 * understood this".
 *
 * Deliberately NOT an iframe of the print route: that route lives outside
 * `_authenticated` and needs a short-lived signed token a logged-in staff member
 * does not have.
 */
export function EventRecordDocument({ payload }: EventRecordDocumentProps) {
  const eventTypeName = String(payload.eventTypeName ?? "Event record");
  const submittedByName = payload.submittedByName
    ? String(payload.submittedByName)
    : null;
  const submittedAt = payload.submittedAt
    ? new Date(String(payload.submittedAt))
    : null;
  const data = (payload.data ?? {}) as Record<string, unknown>;

  let schema: EventFieldSchema | null = null;
  try {
    schema = parseFieldSchema(payload.fieldSchema);
  } catch {
    // A malformed schema must not blank the page: the raw values below are
    // still the record, and a staff member being asked to sign needs to see
    // something rather than an error.
    schema = null;
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full flex-col gap-1">
        <span className="text-heading-3 font-heading-3 text-default-font">
          {eventTypeName}
        </span>
        <span className="text-caption font-caption text-subtext-color">
          {submittedAt
            ? `Submitted ${format(submittedAt, "d MMM yyyy, HH:mm")}`
            : "Submitted"}
          {submittedByName ? ` by ${submittedByName}` : ""}
        </span>
      </div>

      <section
        className="flex w-full flex-col items-start gap-3 rounded-md border border-neutral-200 p-4"
        data-testid="ack-document-event-record"
      >
        <dl className="grid w-full grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
          {schema
            ? schema.fields.map((field) => (
                <div key={field.key} className="flex flex-col gap-0.5">
                  <dt className="text-caption font-caption text-subtext-color">
                    {field.label}
                  </dt>
                  <dd className="text-body font-body text-default-font whitespace-pre-wrap">
                    {formatFieldValue(field, data[field.key])}
                  </dd>
                </div>
              ))
            : Object.entries(data).map(([key, value]) => (
                <div key={key} className="flex flex-col gap-0.5">
                  <dt className="text-caption font-caption text-subtext-color">
                    {key}
                  </dt>
                  <dd className="text-body font-body text-default-font whitespace-pre-wrap">
                    {value === null || value === undefined || value === ""
                      ? "—"
                      : String(value)}
                  </dd>
                </div>
              ))}
        </dl>
      </section>
    </div>
  );
}
